import React, { useEffect, useState, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  UilUser,
  UilEnvelopeAlt,
  UilPhoneAlt,
  UilMapPin,
  UilUserLocation,
  UilLockAccess,
  UilRedo,
} from "@iconscout/react-unicons";
import { FormInputs } from "../../components/inputs/FormInputs";
import { UserContext } from "../../context/user.context";
import Notification from "./Notification";
import LocationSelectorForRegistration from "../../components/deliveryLoctions/LocationSelectorForRegistration";
import "./dropdown.css";
import toast from "react-hot-toast";

import { useAuth } from "../../context/auth.context";

const CreateUser = () => {
  const { setCurrentUser } = useContext(UserContext);
  const navigate = useNavigate();

  // API Base URL

  const { refreshAuth } = useAuth();

  // UI state
  const [disabled, setDisabled] = useState(true);
  const [buttonLabel, setButtonLabel] = useState("Get OTP");
  const [errorMessage, setErrorMessage] = useState("");
  const [notifyType, setNotifyType] = useState("");
  const [showPopUp, setShowPopUp] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showResendOTP, setShowResendOTP] = useState(false); // NEW: Show resend button
  const [resendCooldown, setResendCooldown] = useState(0); // NEW: Cooldown timer

  // Form data
  const [values, setValues] = useState({
    fullname: "",
    email: "",
    phone: "",
    verifyOTP: "",
    buildingName: "",
    flatNumber: "",
    landmark: "",
  });

  function formatCooldown(totalSeconds) {
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    if (minutes > 0) {
      return `${minutes}m ${seconds}s`;
    }
    return `${seconds}s`;
  }

  const [address, setAddress] = useState("");

  // Location values (from dropdown selector)
  const [locationValues, setLocationValues] = useState({
    country: null,
    state: null,
    city: null,
    area: null,
  });

  // Form validation patterns
  const input = [
    {
      id: 1,
      name: "fullname",
      type: "text",
      placeholder: "Full Name",
      pattern: "^[A-Z][a-zA-Z '.-]*[A-Za-z][^-]$",
      errorMessage: "Enter your Name",
      label: "",
      required: true,
    },
    {
      id: 2,
      name: "email",
      type: "email",
      placeholder: "Email Address",
      pattern: "[a-z0-9._%+/-]+@[a-z0-9.-]+.[a-z]{2,}$",
      errorMessage: "Enter your Email Address",
      label: "",
      required: true,
    },
    {
      id: 3,
      name: "phone",
      type: "text",
      placeholder: "501234567",
      pattern: "^[0-9]{9,9}$",
      errorMessage: "Enter 9 digits of Mobile Number",
      label: "",
      required: true,
    },
    {
      id: 4,
      name: "verifyOTP",
      type: "password",
      placeholder: "Please enter your 6 Digits OTP",
      pattern: "^[0-9]{6,6}$",
      errorMessage: "Enter 6 Digits OTP",
      label: "",
      required: true,
    },
  ];

  // Auto-hide notification after 3 seconds
  useEffect(() => {
    if (showPopUp) {
      const timer = setTimeout(() => {
        setShowPopUp(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [showPopUp]);

  // NEW: Cooldown timer for resend OTP
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => {
        setResendCooldown(resendCooldown - 1);
      }, 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  /**
   * Handle location change from LocationSelectorForRegistration
   */
  const handleLocationChange = (location) => {
    setLocationValues(location);
  };

  /**
   * Handle input changes
   */
  const onChangeInputs = (e) => {
    setValues({ ...values, [e.target.name]: e.target.value });

    // Reset OTP button when phone changes
    if (e.target.name === "phone") {
      setButtonLabel("Get OTP");
      setDisabled(true);
      setShowResendOTP(false);
    }

    setErrorMessage("");
  };

  /**
   * Request OTP - Calls backend to generate and send OTP
   */
  const sendOTP = async () => {
    try {
      setIsSubmitting(true);

      const API_BASE = process.env.REACT_APP_ZULAL_API_URL || ""; // Use env variable or default to empty string

      const response = await fetch(
        `${process.env.REACT_APP_ZULAL_API_URL}/api/public/registration/request-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include", // Important: Include cookies for session
          body: JSON.stringify({
            fullname: values.fullname,
            email: values.email,
            phone: values.phone,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        setShowPopUp(true);
        setErrorMessage("OTP sent successfully to your mobile");
        toast.success("OTP sent successfully to your mobile");
        setNotifyType("success");
        setButtonLabel("Verify OTP");
        setDisabled(false);
        setShowResendOTP(true); // NEW: Show resend option
        setResendCooldown(300); // NEW: 60 second cooldown
      } else {
        setShowPopUp(true);
        setErrorMessage(data.error || "Failed to send OTP");
        setNotifyType("error");
        toast.error(data.error || "Failed to send OTP");
      }
    } catch (error) {
      console.error("Error requesting OTP:", error);
      setShowPopUp(true);
      setErrorMessage("Failed to send OTP. Please try again.");
      setNotifyType("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * NEW: Resend OTP - Request new OTP without clearing form
   */
  const resendOTP = async () => {
    if (resendCooldown > 0) {
      setShowPopUp(true);
      setErrorMessage(
        `Please wait ${resendCooldown} seconds before requesting new OTP`
      );
      setNotifyType("error");
      return;
    }

    try {
      setIsSubmitting(true);

      // Clear previous OTP input
      setValues({ ...values, verifyOTP: "" });

      const response = await fetch(
        `${process.env.REACT_APP_ZULAL_API_URL}/api/public/registration/request-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include",
          body: JSON.stringify({
            fullname: values.fullname,
            email: values.email,
            phone: values.phone,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        setShowPopUp(true);
        setErrorMessage("New OTP sent successfully to your mobile");
        setNotifyType("success");
        setButtonLabel("Verify OTP"); // Reset to verify OTP
        setDisabled(false);
        setResendCooldown(60); // Reset cooldown
        toast.success("New OTP sent successfully to your mobile");
      } else {
        setShowPopUp(true);
        setErrorMessage(data.error || "Failed to resend OTP");
        setNotifyType("error");
        toast.error(data.error || "Failed to resend OTP");
      }
    } catch (error) {
      console.error("Error resending OTP:", error);
      setShowPopUp(true);
      setErrorMessage("Failed to resend OTP. Please try again.");
      setNotifyType("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Verify OTP - Calls backend to verify OTP
   */
  const verifyOTP = async () => {
    try {
      setIsSubmitting(true);

      const response = await fetch(
        `${process.env.REACT_APP_ZULAL_API_URL}/api/public/registration/verify-otp`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include", // Important: Include cookies for session
          body: JSON.stringify({
            phone: values.phone,
            otp: values.verifyOTP,
          }),
        }
      );

      const data = await response.json();

      if (data.success) {
        setShowPopUp(true);
        setErrorMessage("OTP verified successfully! Click to Create Account");
        toast.success("OTP verified successfully! Click to Create Account");
        setNotifyType("success");
        setButtonLabel("Create Account");
        setShowResendOTP(false); // NEW: Hide resend after successful verification
      } else {
        setShowPopUp(true);
        setErrorMessage(data.error || "Invalid OTP");
        setNotifyType("error");

        // NEW: If OTP expired/invalid, allow resend
        if (data.error.includes("expired") || data.error.includes("Invalid")) {
          setShowResendOTP(true);
          toast.error("OTP expired or invalid. Please request a new OTP.");
        }
      }
    } catch (error) {
      console.error("Error verifying OTP:", error);
      setShowPopUp(true);
      setErrorMessage("Failed to verify OTP. Please try again.");
      setNotifyType("error");
      toast.error("Failed to verify OTP. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Create new user - Calls backend to register user
   */
  const createNewUser = async () => {
    try {
      setIsSubmitting(true);

      // Validate location
      if (
        !locationValues.country?.countryId ||
        !locationValues.state?.stateId ||
        !locationValues.city?.cityId ||
        !locationValues.area?.areaId
      ) {
        setErrorMessage(
          "Please select Country, State, City and Area from the dropdown"
        );
        setShowPopUp(true);
        setNotifyType("error");
        setIsSubmitting(false);
        return;
      }

      const userData = {
        fullname: values.fullname,
        email: values.email,
        phone: values.phone,
        buildingName: values.buildingName,
        flatNumber: values.flatNumber,
        landmark: values.landmark,
        address: address,
        country: locationValues.country,
        state: locationValues.state,
        city: locationValues.city,
        area: locationValues.area,
      };

      const response = await fetch(
        `${process.env.REACT_APP_ZULAL_API_URL}/api/public/registration/create-user`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          credentials: "include", // Important: Include session cookie
          body: JSON.stringify(userData),
        }
      );

      const data = await response.json();

      if (data.success) {
        setShowPopUp(true);
        setErrorMessage("Account created successfully!");
        setNotifyType("success");

        setCurrentUser({
          customerCode: data.data.customerCode,
          outletName: values.fullname,
          email: values.email,
          phoneNo: values.phone,
          customerAddress: address,
          isloggedIn: true,
        });

        const authResult = await refreshAuth();

        if (authResult.success) {
          navigate("/user/dashboard", { state: { PageTitle: "Dashboard" } });
        } else {
          // fallback — cookie somehow didn't take; let them log in manually
          setButtonLabel("Login to Account");
        }
      } else {
        setShowPopUp(true);
        setErrorMessage(data.error || "Failed to create account");
        setNotifyType("error");

        // NEW: If session expired, show resend OTP
        if (data.error.includes("OTP") || data.error.includes("expired")) {
          setButtonLabel("Verify OTP");
          setShowResendOTP(true);
        }
      }
    } catch (error) {
      console.error("Error creating user:", error);
      setShowPopUp(true);
      setErrorMessage("Failed to create account. Please try again.");
      setNotifyType("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  /**
   * Form submission handler
   */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isSubmitting) return; // Prevent double submission

    if (buttonLabel === "Get OTP") {
      await sendOTP();
    } else if (buttonLabel === "Verify OTP") {
      await verifyOTP();
    } else if (buttonLabel === "Create Account") {
      await createNewUser();
    } else if (buttonLabel === "Login to Account") {
      navigate("/user/dashboard", { state: { PageTitle: "Dashboard" } });
    }
  };

  return (
    <div className="mb-40">
      {/* Header */}
      <div className="bg-pagePattern bg-center">
        <div className="flex mx-auto max-w-screen-lg text-center justify-center items-center mb-9 h-32">
          <label className="text-7xl text-textWhite font-medium">
            New User
          </label>
        </div>
      </div>

      {/* Instructions */}
      <div className="mx-auto max-w-screen-sm md:max-w-2xl lg:max-w-3xl justify-start items-start text-navarbarText text-3xl font-poppins font-medium mb-8">
        <p className="ml-2 mr-2">New Account</p>
        <p className="text-base ml-2 mr-2 text-darkTextColor font-poppins font-normal">
          Please enter your Basic Information with Address to serve you better.
        </p>
      </div>

      {/* Form */}
      <div className="mx-auto max-w-screen-sm md:max-w-2xl lg:max-w-3xl text-center justify-center items">
        <div className="mx-auto max-w-sm ml-4 mr-4 mt-4 md:max-w-2xl lg:max-w-3xl justify-center items-center">
          <div className="text-left">
            <span className="text-2xl text-primary font-poppins font-semibold">
              Account Details
            </span>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="mx-auto grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
              {/* Name */}
              <div className="flex flex-row mt-2">
                <div className="bg-darkVeryLight ring-1 ring-secondary h-10 px-4 bg-zinc-100 rounded-tl-md rounded-bl-md justify-start items-center gap-1 inline-flex">
                  <UilUser className="text-gray-500 w-4" />
                  <div className="font-normal text-C12 w-12">Name</div>
                </div>
                <div className="mx-auto justify-center w-full">
                  <FormInputs
                    name="fullname"
                    type="text"
                    placeholder="Full Name"
                    autoFocus={true}
                    value={values.fullname}
                    {...input[0]}
                    onChange={onChangeInputs}
                  />
                </div>
              </div>

              {/* Email */}
              <div className="flex flex-row mt-2">
                <div className="bg-darkVeryLight ring-1 ring-secondary h-10 px-4 bg-zinc-100 rounded-tl-md rounded-bl-md justify-start items-center gap-1 inline-flex">
                  <UilEnvelopeAlt className="text-gray-500 w-4" />
                  <div className="font-normal text-C12 w-12">Email</div>
                </div>
                <div className="mx-auto justify-center w-full">
                  <FormInputs
                    name="email"
                    value={values.email}
                    {...input[1]}
                    onChange={onChangeInputs}
                  />
                </div>
              </div>

              {/* Phone */}
              <div className="flex flex-row mt-2">
                <div className="bg-darkVeryLight ring-1 ring-secondary h-10 px-4 bg-zinc-100 rounded-tl-md rounded-bl-md justify-start items-center gap-1 inline-flex">
                  <UilPhoneAlt className="text-gray-500 w-4" />
                  <div className="font-normal text-C12 w-12">+971</div>
                </div>
                <div className="mx-auto justify-center w-full">
                  <FormInputs
                    name="phone"
                    pattern="[0-9]{9}"
                    value={values.phone}
                    {...input[2]}
                    onChange={onChangeInputs}
                  />
                </div>
              </div>

              {/* OTP */}
              <div className="flex flex-row mt-2">
                <div className="bg-darkVeryLight ring-1 ring-secondary h-10 px-4 bg-zinc-100 rounded-tl-md rounded-bl-md justify-start items-center gap-1 inline-flex">
                  <UilLockAccess className="w-4 text-gray-500" />
                  <div className="font-normal text-C12 w-12">OTP</div>
                </div>
                <div className="mx-auto justify-center w-full">
                  <FormInputs
                    name="verifyOTP"
                    disabled={disabled}
                    value={values.verifyOTP}
                    {...input[3]}
                    onChange={onChangeInputs}
                  />
                </div>
              </div>

              {/* Location Selector - NOW USING SECURE BACKEND ENDPOINTS */}
              <div className="col-span-1 md:col-span-2">
                <LocationSelectorForRegistration
                  onLocationChange={handleLocationChange}
                  initialLocationValues={locationValues}
                />
              </div>

              {/* Building Name */}
              <div className="flex flex-row mt-2">
                <div className="bg-darkVeryLight ring-1 ring-secondary h-10 px-4 bg-zinc-100 rounded-tl-md rounded-bl-md justify-start items-center gap-1 inline-flex">
                  <UilMapPin className="text-gray-500 w-4" />
                  <div className="font-normal text-C12 w-12">Building</div>
                </div>
                <div className="mx-auto justify-center w-full">
                  <input
                    id="buildingName"
                    name="buildingName"
                    type="text"
                    value={values.buildingName}
                    onChange={(e) =>
                      setValues({ ...values, buildingName: e.target.value })
                    }
                    className="w-full px-4 ring-1 ring-secondary text-sm text-gray-900 bg-white border-0 dark:bg-gray-800 focus:ring-1 focus:ring-primary focus:border-primaryHover rounded-tr-md rounded-br-md dark:text-white dark:placeholder-gray-400"
                    placeholder="Enter the Building Name..."
                    required
                  />
                </div>
              </div>

              {/* Flat Number */}
              <div className="flex flex-row mt-2">
                <div className="bg-darkVeryLight ring-1 ring-secondary h-10 px-4 bg-zinc-100 rounded-tl-md rounded-bl-md justify-start items-center gap-1 inline-flex">
                  <UilMapPin className="text-gray-500 w-4" />
                  <div className="font-normal text-C12 w-12">Flat #</div>
                </div>
                <div className="mx-auto justify-center w-full">
                  <input
                    id="flatNumber"
                    name="flatNumber"
                    type="text"
                    value={values.flatNumber}
                    onChange={(e) =>
                      setValues({ ...values, flatNumber: e.target.value })
                    }
                    className="w-full px-4 ring-1 ring-secondary text-sm text-gray-900 bg-white border-0 dark:bg-gray-800 focus:ring-1 focus:ring-primary focus:border-primaryHover rounded-tr-md rounded-br-md dark:text-white dark:placeholder-gray-400"
                    placeholder="Enter the Flat Number..."
                    required
                  />
                </div>
              </div>

              {/* Landmark */}
              <div className="flex flex-row mt-2">
                <div className="bg-darkVeryLight ring-1 ring-secondary h-10 px-4 bg-zinc-100 rounded-tl-md rounded-bl-md justify-start items-center gap-1 inline-flex">
                  <UilMapPin className="text-gray-500 w-4" />
                  <div className="font-normal text-C12 w-12">Landmark</div>
                </div>
                <div className="mx-auto justify-center w-full">
                  <input
                    id="landmark"
                    name="landmark"
                    type="text"
                    value={values.landmark}
                    onChange={(e) =>
                      setValues({ ...values, landmark: e.target.value })
                    }
                    className="w-full px-4 ring-1 ring-secondary text-sm text-gray-900 bg-white border-0 dark:bg-gray-800 focus:ring-1 focus:ring-primary focus:border-primaryHover rounded-tr-md rounded-br-md dark:text-white dark:placeholder-gray-400"
                    placeholder="Enter the Nearest Landmark..."
                    required
                  />
                </div>
              </div>

              {/* Address */}
              <div className="flex flex-row mt-2 md:col-span-2">
                <div className="bg-darkVeryLight ring-1 ring-secondary h-10 px-4 bg-zinc-100 rounded-tl-md rounded-bl-md justify-start items-center gap-1 inline-flex">
                  <UilUserLocation className="text-gray-500 w-4" />
                  <div className="font-normal text-C12 w-12">Address</div>
                </div>
                <div className="mx-auto justify-center w-full">
                  <textarea
                    id="address"
                    rows="2"
                    value={address}
                    onChange={(e) => setAddress(e.target.value)}
                    className="w-full px-4 ring-1 ring-secondary text-sm text-gray-900 bg-white border-0 dark:bg-gray-800 focus:ring-1 focus:ring-primary focus:border-primaryHover rounded-tr-md rounded-br-md dark:text-white dark:placeholder-gray-400"
                    placeholder="Enter your complete address..."
                    required
                  ></textarea>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="mx-auto max-w-screen-sm text-center justify-center items-center mt-4">
              <button
                id="buttonSubmit"
                name="buttonSubmit"
                type="submit"
                disabled={isSubmitting}
                className={`h-14 rounded-md w-80 sm:w-full text-white font-poppins text-base ${
                  isSubmitting
                    ? "bg-gray-400 cursor-not-allowed"
                    : "bg-secondary hover:bg-secondaryHover"
                }`}
              >
                {isSubmitting ? "Processing..." : buttonLabel}
              </button>

              {/* NEW: Resend OTP Button */}
              {showResendOTP && buttonLabel === "Verify OTP" && (
                <button
                  type="button"
                  onClick={resendOTP}
                  disabled={resendCooldown > 0 || isSubmitting}
                  className={`mt-3 h-12 rounded-md w-80 sm:w-full font-poppins text-sm flex items-center justify-center gap-2 ${
                    resendCooldown > 0 || isSubmitting
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-white text-secondary border-2 border-secondary hover:bg-secondary hover:text-white"
                  }`}
                >
                  <UilRedo className="w-4 h-4" />
                  {resendCooldown > 0
                    ? `Resend OTP in ${formatCooldown(resendCooldown)}s`
                    : "Resend OTP"}
                </button>
              )}

              {/* Notification */}
              <div>
                <Notification
                  message={errorMessage}
                  notifyType={notifyType}
                  showPopup={showPopUp}
                  onClose={() => setShowPopUp(false)}
                />
              </div>

              {/* Error message */}
              {errorMessage && !showPopUp && (
                <span className="text-textRed text-base">{errorMessage}</span>
              )}

              {/* Login link */}
              <div className="mt-2 text-gray-500 text-base">
                Already Registered with us? &nbsp;
                <Link
                  to={"../user/dashboard"}
                  className="font-medium text-primary dark:text-blue-500 hover:underline"
                  state={{ PageTitle: "Dashboard" }}
                >
                  Login
                </Link>
              </div>
            </div>
            <div className="mb-80"></div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateUser;
